import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, ArrowUpRight, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { notify } from "../lib/notify";

// ---- Public S$NEER Embed Widget (renders bare — for iframe consumption) ----
export function SNEEREmbed({ theme: forcedTheme }) {
  const [sneer, setSneer] = useState(null);
  useEffect(() => {
    api.get("/sneer").then(r => setSneer(r.data)).catch(() => {});
    const t = setInterval(() => api.get("/sneer").then(r => setSneer(r.data)).catch(() => {}), 60_000 * 10);
    return () => clearInterval(t);
  }, []);

  // Theme is now controlled by ThemeProvider (lib/theme.jsx), which already
  // honours the ?theme=light|dark query param at initial state. No-op here to
  // avoid a child→parent useEffect race that previously reverted the theme.

  if (!sneer) return <div className="p-6 text-sm text-slate-400 font-mono">Loading S$NEER…</div>;

  const change = sneer.change_30d_bps;
  const up = change >= 0;

  return (
    <div className="font-sans" data-testid="sneer-embed-widget" style={{ background: "var(--rp-card-bg)", border: "1px solid var(--rp-border)", borderRadius: 4, padding: 20, maxWidth: 520 }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="font-display text-base font-bold" style={{ color: "var(--rp-primary)" }}>RiskPedia</div>
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm" style={{ background: "var(--rp-primary)", color: "#fff" }}>SG</span>
          <span className="overline ml-2">S$NEER</span>
        </div>
        <a href="https://sg-riskguide.preview.emergentagent.com/money-flow" target="_blank" rel="noopener" className="text-[10px] font-mono inline-flex items-center gap-0.5" style={{ color: "var(--rp-ink-3)" }}>
          riskpedia.sg <ExternalLink size={10} />
        </a>
      </div>

      <div className="mt-5 flex items-baseline gap-3">
        <div className="font-display text-5xl font-bold" style={{ color: "var(--rp-primary)" }}>{sneer.current.toFixed(2)}</div>
        <div className={`font-mono text-sm ${up ? "text-emerald-600" : "text-rose-600"}`}>
          {up ? "▲" : "▼"} {up ? "+" : ""}{change} bps · 30d
        </div>
      </div>

      <div className="mt-3 text-xs leading-relaxed" style={{ color: "var(--rp-ink-2)" }}>{sneer.mas_stance}</div>

      <div className="mt-5 h-12 flex items-end gap-px">
        {(sneer.series || []).map((p, i, arr) => {
          const min = Math.min(...arr.map(x => x.value));
          const max = Math.max(...arr.map(x => x.value));
          const range = Math.max(0.001, max - min);
          const h = 4 + ((p.value - min) / range) * 44;
          return <div key={i} className="flex-1" style={{ height: `${h}px`, background: i === arr.length - 1 ? "var(--rp-accent)" : "var(--rp-primary)", opacity: i === arr.length - 1 ? 1 : 0.35 + (i / arr.length) * 0.55 }} />;
        })}
      </div>

      <div className="mt-4 flex items-center justify-between text-[10px] font-mono" style={{ color: "var(--rp-ink-3)" }}>
        <span>Next MPS · {sneer.next_mps}</span>
        <span>Source: {sneer.source === "alphavantage_basket_proxy" ? "Alpha Vantage" : "Indicative"}</span>
      </div>

      <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--rp-border)" }}>
        <div className="text-[10px]" style={{ color: "var(--rp-ink-3)" }}>Educational only — not investment or regulatory advice.</div>
      </div>
    </div>
  );
}

// ---- Embed page (raw layout, no nav/footer) ----
export function SNEEREmbedPage() {
  return (
    <div style={{ background: "transparent", padding: 12, minHeight: "100vh" }}>
      <SNEEREmbed />
    </div>
  );
}

// ---- Embed marketing page with copy snippet ----
export function EmbedMarketing() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://sg-riskguide.preview.emergentagent.com";
  const lightSnippet = `<iframe
  src="${origin}/embed/sneer?theme=light"
  width="560" height="320"
  style="border:0; overflow:hidden;"
  loading="lazy"
  title="RiskPedia SG · S$NEER">
</iframe>`;
  const darkSnippet = lightSnippet.replace("theme=light", "theme=dark");

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => notify.success("Embed code copied."));
  };

  return (
    <div data-testid="page-embed">
      <section className="border-b border-slate-200">
        <div className="container-rp py-16 lg:py-24">
          <div className="overline">Free for blogs · podcasts · slide decks</div>
          <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 tracking-tight">Embed the S$NEER.</h1>
          <p className="text-slate-600 mt-4 max-w-2xl text-lg leading-relaxed">A live trade-weighted SGD index widget you can drop into any Singapore finance blog, fintech newsletter or podcast page. Auto-refreshes every 10 minutes. No signup, no API key.</p>
        </div>
      </section>

      <section className="section">
        <div className="container-rp grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-8">
            <div>
              <div className="overline">01 · Light theme</div>
              <h2 className="font-display text-2xl font-semibold mt-2">Copy this snippet</h2>
              <pre className="mt-4 rp-card p-4 overflow-x-auto text-xs font-mono leading-relaxed">{lightSnippet}</pre>
              <button onClick={() => copy(lightSnippet)} className="btn-outline mt-3" data-testid="embed-copy-light"><Copy size={14} /> Copy light theme</button>
            </div>
            <div>
              <div className="overline">02 · Dark theme</div>
              <h2 className="font-display text-2xl font-semibold mt-2">Or this one</h2>
              <pre className="mt-4 rp-card p-4 overflow-x-auto text-xs font-mono leading-relaxed">{darkSnippet}</pre>
              <button onClick={() => copy(darkSnippet)} className="btn-outline mt-3" data-testid="embed-copy-dark"><Copy size={14} /> Copy dark theme</button>
            </div>
            <div>
              <div className="overline">03 · What you get</div>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li>· Live trade-weighted SGD index value</li>
                <li>· 30-day spark chart</li>
                <li>· MAS policy stance copy (updated each MPS)</li>
                <li>· Source attribution to RiskPedia SG with a backlink</li>
              </ul>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="sticky top-24">
              <div className="overline-muted mb-3">Live preview</div>
              <SNEEREmbed />
              <p className="text-xs text-slate-500 mt-4 leading-relaxed">Free to use. Single requirement: keep the "riskpedia.sg" link visible. Want a co-branded version for media partners? <Link to="/contact" className="font-medium" style={{ color: "var(--rp-accent)" }}>Get in touch</Link>.</p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
